import {
  Box,
  HStack,
  Separator,
  Table,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LocalProduct } from "../types/LocalProduct";
import { FiPackage, FiRefreshCw, FiTrendingUp } from "react-icons/fi";
import { formatDate } from "@/utils/formatDate";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";

interface LocalProductInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  localProduct: LocalProduct;
}

const headerProps = {
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "wider",
  textTransform: "uppercase",
  color: "fg.muted",
} as const;

const LocalProductInventoryModal = ({
  isOpen,
  onClose,
  localProduct,
}: LocalProductInventoryModalProps) => {
  const {
    inventoryDetails,
    product: { productAdjustments, ...product },
  } = localProduct;

  const initialStock = inventoryDetails.find(
    (detail) => !detail.replenishmentId,
  );
  const stockInicial = initialStock?.stock || 0;
  const precioUnitario = initialStock?.price || 0;

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-start"
          pe={12}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <Box>
            <DialogTitle>Detalles de inventario</DialogTitle>
            <Text color="fg.muted" fontSize="sm" fontWeight="normal" mt={1}>
              {product.description}
            </Text>
          </Box>
          <Box textAlign="end">
            <Text color="fg.muted" fontSize="xs" textTransform="uppercase">
              Stock actual
            </Text>
            <Text fontWeight="bold" fontSize="lg">
              {product.stock}
            </Text>
          </Box>
        </DialogHeader>
        <DialogBody pt={4}>
          <Tabs.Root defaultValue="replenishments" colorPalette="brand">
            <Tabs.List>
              <Tabs.Trigger value="initial">
                <FiPackage />
                Stock inicial
              </Tabs.Trigger>
              <Tabs.Trigger value="replenishments">
                <FiTrendingUp />
                Reposiciones
              </Tabs.Trigger>
              <Tabs.Trigger value="adjustments">
                <FiRefreshCw />
                Reajustes
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="initial">
              <VStack gap={4} align="stretch" py={2}>
                <HStack justify="space-between" align="center">
                  <Box>
                    <Text fontSize="lg" fontWeight="semibold">
                      Configuración inicial
                    </Text>
                    <Text fontSize="sm" color="fg.muted">
                      Stock registrado al crear el producto
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="fg.muted">
                    {formatDate(localProduct.createdAt)}
                  </Text>
                </HStack>
                <Separator />
                <HStack gap={8} justify="space-around" py={4}>
                  <VStack gap={1} align="start">
                    <Text fontSize="sm" color="fg.muted">
                      Stock inicial
                    </Text>
                    <Text fontSize="3xl" fontWeight="bold" color="brand.fg">
                      {stockInicial}
                    </Text>
                  </VStack>
                  <VStack gap={1} align="start">
                    <Text fontSize="sm" color="fg.muted">
                      Precio unitario
                    </Text>
                    <Text fontSize="3xl" fontWeight="bold">
                      ${precioUnitario.toFixed(2)}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </Tabs.Content>

            <Tabs.Content value="replenishments">
              <Box borderWidth="1px" borderColor="border" rounded="lg">
                <Table.Root size="sm" variant="line">
                  <Table.Header bg="bg.subtle">
                    <Table.Row>
                      <Table.ColumnHeader {...headerProps}>
                        Stock
                      </Table.ColumnHeader>
                      <Table.ColumnHeader {...headerProps}>
                        Precio
                      </Table.ColumnHeader>
                      <Table.ColumnHeader {...headerProps}>
                        Stock usado
                      </Table.ColumnHeader>
                      <Table.ColumnHeader {...headerProps}>
                        ID de Reposición
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {inventoryDetails.map((detail, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>{detail.stock}</Table.Cell>
                        <Table.Cell>${detail.price}</Table.Cell>
                        <Table.Cell>{detail.usedStock}</Table.Cell>
                        <Table.Cell>
                          {detail.replenishmentId ? (
                            <Text
                              as="span"
                              fontFamily="mono"
                              fontSize="xs"
                              title={detail.replenishmentId}
                            >
                              {detail.replenishmentId}
                            </Text>
                          ) : (
                            <Text as="i" color="fg.muted">
                              Stock inicial
                            </Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {inventoryDetails.length === 0 && (
                      <Table.Row>
                        <Table.Cell
                          colSpan={4}
                          textAlign="center"
                          py={8}
                          color="fg.muted"
                        >
                          No hay detalles de inventario disponibles
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            </Tabs.Content>

            <Tabs.Content value="adjustments">
              <Box borderWidth="1px" borderColor="border" rounded="lg">
                <Table.Root size="sm" variant="line">
                  <Table.Header bg="bg.subtle">
                    <Table.Row>
                      <Table.ColumnHeader {...headerProps}>
                        Fecha
                      </Table.ColumnHeader>
                      <Table.ColumnHeader {...headerProps}>
                        Motivo
                      </Table.ColumnHeader>
                      <Table.ColumnHeader {...headerProps} textAlign="end">
                        Ajuste
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {productAdjustments?.map((productAdjustment, index) => (
                      <Table.Row key={index}>
                        <Table.Cell>
                          {formatDate(productAdjustment.createdAt)}
                        </Table.Cell>
                        <Table.Cell>
                          {productAdjustment.productAdjustmentType?.name}
                        </Table.Cell>
                        <Table.Cell textAlign="end" fontWeight="semibold">
                          {productAdjustment.adjustmentProduct?.quantity}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {!productAdjustments?.length && (
                      <Table.Row>
                        <Table.Cell
                          colSpan={3}
                          textAlign="center"
                          py={8}
                          color="fg.muted"
                        >
                          No hay reajustes registrados
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            </Tabs.Content>
          </Tabs.Root>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default LocalProductInventoryModal;
